// Issue trigger resource patterns are globs that select resources by name, e.g.
// `prod-*` or `prod-*-api`. `*` matches any run of characters, anywhere in the
// pattern. Prefixing a pattern with `!` negates it, matching every resource
// except those whose name matches the rest of the pattern.

export const NEGATION_PREFIX = '!';
export const WILDCARD = '*';

export interface ParsedResourcePattern {
  is_negated: boolean;
  pattern: string;
}

export const parseResourcePattern = (value: string): ParsedResourcePattern => {
  const is_negated = value.startsWith(NEGATION_PREFIX);
  return {
    is_negated,
    pattern: is_negated ? value.slice(NEGATION_PREFIX.length) : value,
  };
};

// Returns why a resource pattern is invalid, or null when it is valid. Only
// negated patterns can be invalid: a malformed negated pattern would otherwise
// match every resource, or none at all.
export const getResourcePatternError = (value: string): string | null => {
  const { is_negated, pattern } = parseResourcePattern(value);

  if (!is_negated) {
    return null;
  }
  if (pattern.trim() === '') {
    return 'Enter a pattern after !';
  }
  if (/^\*+$/.test(pattern)) {
    return 'Excluding * would match nothing';
  }
  if (pattern.includes(NEGATION_PREFIX)) {
    return 'Use ! only once, at the start of the pattern';
  }
  return null;
};

// Whether a name matches a positive glob pattern (without the `!` prefix).
// Matching is case-insensitive, and a pattern without a wildcard must match the
// name exactly. The literal segments between wildcards are located left to right
// with indexOf rather than compiled to a regex, so matching cannot backtrack.
export const matchesPattern = (name: string, pattern: string): boolean => {
  name = name.toLowerCase();
  const segments = pattern.toLowerCase().split(WILDCARD);

  if (segments.length === 1) {
    return name === segments[0];
  }

  const first_segment = segments[0];
  const last_segment = segments[segments.length - 1];

  if (!name.startsWith(first_segment)) {
    return false;
  }

  // Taking the leftmost occurrence of each middle segment leaves the most room
  // for the rest of the pattern, so a greedy scan finds a match if one exists.
  let position = first_segment.length;
  for (const segment of segments.slice(1, -1)) {
    const index = name.indexOf(segment, position);
    if (index === -1) {
      return false;
    }
    position = index + segment.length;
  }

  return name.length - last_segment.length >= position && name.endsWith(last_segment);
};

// Whether a resource name is selected by a resource pattern, negated or not.
// Negated patterns fail closed: an invalid pattern or an unknown resource name
// selects nothing, so an exclusion can never silently widen into a catch-all.
export const matchesResourcePattern = (
  name: string | null | undefined,
  value: string
): boolean => {
  if (!name) {
    return value === WILDCARD;
  }

  const { is_negated, pattern } = parseResourcePattern(value);

  if (!is_negated) {
    return matchesPattern(name, pattern);
  }

  if (getResourcePatternError(value)) {
    return false;
  }

  return !matchesPattern(name, pattern);
};

// Dashboard input shorthand: a pattern without a wildcard is treated as
// "contains", so `prod` becomes `*prod*` and `!prod` becomes `!*prod*`.
export const expandResourcePatternShorthand = (value: string): string => {
  if (!value) {
    return value;
  }

  const { is_negated, pattern } = parseResourcePattern(value);

  if (pattern === '' || pattern.includes(WILDCARD)) {
    return value;
  }

  return `${is_negated ? NEGATION_PREFIX : ''}${WILDCARD}${pattern}${WILDCARD}`;
};
